import {Divider} from '@/components/ui/divider/Divider'
import {useTranslation} from '@/translations/i18n'
import {MedicalReportSectionTitle} from '../medical-report-section-title/MedicalReportSectionTitle'
import {Controller, useFormContext, useWatch} from 'react-hook-form'
import {MedicalReportTextAreaSections} from '../medical-report-text-area-sections/MedicalReportTextAreaSections'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {closeProjectReasonsMapToLabel, closeProjectReportToSection} from '../../utils'
import {CloseProjectReportsSchema, MedicalReportFieldsSchema} from '../../formSchemas'
import {z} from 'zod'
import {InputSelect} from '@/components/commons/input-select/InputSelect'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {MedicalReportEndDateField} from '@/features/medical-reports/components/medical-report-end-date-field/MedicalReportEndDateField'
import {MedicalReportTextArea} from '../medical-report-text-area/MedicalReportTextArea'
import {REPORT_TEXTAREA_LIMIT} from '@/utilities/helpers'

const CloseContinuosProjectAdditionalInfoSchema = MedicalReportFieldsSchema.pick({
    closureReason: true,
    endDate: true,
    project: true,
    notes: true
})
type CloseContinuosProjectAdditionalInfoSchema = z.infer<typeof CloseContinuosProjectAdditionalInfoSchema>

export const CloseContinuousProjectSections = () => (
    <>
        <ReportSections />
        <Divider direction="horizontal" />
        <AdditionalInfoSectionForm />
    </>
)

const ReportSections = () => {
    const {t} = useTranslation()
    return (
        <>
            <MedicalReportSectionTitle>{t('commons:report', {count: 1})}</MedicalReportSectionTitle>
            <MedicalReportTextAreaSections
                sectionsMap={closeProjectReportToSection}
                schema={CloseProjectReportsSchema}
            />
        </>
    )
}

const AdditionalInfoSectionForm = () => {
    const {t} = useTranslation()
    const form = useFormContext<CloseContinuosProjectAdditionalInfoSchema>()
    const watchClosureReason = useWatch({control: form.control, name: 'closureReason'})

    return (
        <>
            <MedicalReportSectionTitle>{t('commons:additional_info')}</MedicalReportSectionTitle>
            <FormSection title={t('medical_records:closure_reason')}>
                <Flexbox gap={2} fullWidth>
                    <Flexbox direction="column" gap={2} fullWidth>
                        <Controller
                            control={form.control}
                            name={'closureReason'}
                            render={({field, fieldState}) => (
                                <InputSelect
                                    label={t('medical_records:select_closure_reason')}
                                    value={field.value ?? undefined}
                                    placeholder={t('medical_records:select_an_option_placeholder')}
                                    optionsMap={closeProjectReasonsMapToLabel}
                                    errorMessage={fieldState.error?.message}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        {watchClosureReason && watchClosureReason === 'other' && (
                            <Controller
                                control={form.control}
                                name="notes"
                                render={({field, fieldState}) => (
                                    <MedicalReportTextArea
                                        rows={4}
                                        maxLength={REPORT_TEXTAREA_LIMIT}
                                        title={t('commons:notes')}
                                        placeholder={t('commons:description_dot')}
                                        errorMessage={fieldState.error?.message}
                                        currentField="notes"
                                        onChange={field.onChange}
                                        value={field.value}
                                        hideExpandableButton
                                    />
                                )}
                            />
                        )}
                    </Flexbox>

                    <MedicalReportEndDateField />
                </Flexbox>
            </FormSection>
        </>
    )
}
